const VISIBLE_SCHEDULED_ITEM_STATUSES = ['scheduled', 'draft'];

const SET_CONVERSATION_HAS_SCHEDULED_EMAIL_MUTATION =
  'SET_CONVERSATION_HAS_SCHEDULED_EMAIL';
const SET_CONVERSATION_HAS_SCHEDULED_MESSAGE_MUTATION =
  'SET_CONVERSATION_HAS_SCHEDULED_MESSAGE';
const SCHEDULED_EMAIL_FLAG_KEY = 'hasScheduledEmail';
const SCHEDULED_MESSAGE_FLAG_KEY = 'hasScheduledMessage';

const normalizeScheduledItemContent = (item = {}) =>
  [item.content, item.text_content, item.html_content, item.body, item.message]
    .find((value) => typeof value === 'string' && value.trim())
    ?.replace(/\s+/g, ' ')
    .trim() || '';

const buildScheduledItemDraftDedupeKey = (item = {}) => {
  const content = normalizeScheduledItemContent(item);
  if (!content) {
    return '';
  }

  const recipientFields = [
    item.to,
    item.to_email,
    item.to_emails,
    item.recipient,
    item.recipient_id,
  ];
  const recipients = recipientFields
    .flat()
    .filter((value) => value !== null && value !== undefined)
    .join(',');

  return `${recipients}::${content}`;
};

const filterDuplicateDraftScheduledItems = (items = []) => {
  const hasScheduledItem = items.some((item) => item?.status === 'scheduled');
  if (hasScheduledItem) {
    return items.filter((item) => item?.status !== 'draft');
  }

  const scheduledKeys = new Set(
    items
      .filter((item) => item?.status === 'scheduled')
      .map(buildScheduledItemDraftDedupeKey)
      .filter(Boolean)
  );

  if (!scheduledKeys.size) {
    return items;
  }

  return items.filter((item) => {
    if (item?.status !== 'draft') {
      return true;
    }

    const draftKey = buildScheduledItemDraftDedupeKey(item);
    return !draftKey || !scheduledKeys.has(draftKey);
  });
};

const filterVisibleScheduledItems = (items = []) => {
  if (!Array.isArray(items)) {
    return [];
  }

  const visibleItems = items.filter((item) =>
    VISIBLE_SCHEDULED_ITEM_STATUSES.includes(item.status)
  );

  return filterDuplicateDraftScheduledItems(visibleItems);
};

const hasVisibleScheduledItems = (items = []) =>
  filterVisibleScheduledItems(items).length > 0;

const hasConversationScheduledEmail = (conversation = null) =>
  conversation?.additional_attributes?.has_scheduled_email === true;

const hasConversationScheduledMessage = (conversation = null) =>
  conversation?.additional_attributes?.has_scheduled_message === true;

const hasConversationScheduledIndicator = (conversation = null) =>
  hasConversationScheduledEmail(conversation) ||
  hasConversationScheduledMessage(conversation);

const hasConversationAiReply = (conversation = null) =>
  conversation?.has_ai_reply === true;

// `has_ai_reply` si spegne solo all'arrivo di un messaggio in entrata
// (Messages::AiReplyStatusResetter), quindi resta acceso anche quando dopo
// Otello ha risposto una persona: dice "l'AI e' intervenuta e l'ospite non ha
// piu' scritto", non "l'ultimo messaggio e' suo". In lista serve la domanda
// stretta, e la risposta e' gia' nel payload: il messaggio porta auto_generated.
const isMessageFromAi = (message = null) => {
  if (!message) return false;

  const messageType = message.message_type;
  const isOutgoing = messageType === 1 || messageType === 'outgoing';
  const isFailed = message.status === 'failed' || Number(message.status) === 3;

  return isOutgoing && !isFailed && message.auto_generated === true;
};

// Il payload della lista in preview mode NON serializza
// last_non_activity_message (solo messages[]): quel campo esiste in store solo
// se un evento ActionCable l'ha popolato. Chi renderizza la riga deve quindi
// interrogare il messaggio risolto che sta mostrando (lastMessageInChat), non
// questo campo.
const isLastMessageFromAi = (conversation = null) =>
  isMessageFromAi(conversation?.last_non_activity_message);

const isConversationGeneratingDraft = (conversation = null) =>
  !!conversation?.isGeneratingDraft;

const AI_SCHEDULED_STATE_ATTRIBUTE_KEY = 'ai_scheduled_state';
const AI_SCHEDULED_STATE_REVISION_ATTRIBUTE_KEY = 'ai_scheduled_state_revision';

// La revision e' persistita dal projector insieme allo stato, monotona per
// conversazione: serve a riconoscere gli snapshot consegnati fuori ordine
// (Sidekiq non garantisce l'ordine di enqueue tra job diversi).
const normalizeConversationAiScheduledStateRevision = (conversation = null) => {
  const revision = Number(
    conversation?.additional_attributes?.[
      AI_SCHEDULED_STATE_REVISION_ATTRIBUTE_KEY
    ]
  );
  return Number.isSafeInteger(revision) && revision > 0 ? revision : null;
};

// Uno snapshot senza revision non e' giudicabile: se in store una revision
// c'e' gia', il dubbio va a favore dello stato mostrato (mai regredire).
const isStaleConversationAiScheduledStateSnapshot = ({
  currentConversation = null,
  incomingConversation = null,
} = {}) => {
  const currentRevision =
    normalizeConversationAiScheduledStateRevision(currentConversation);
  if (currentRevision === null) return false;

  const incomingRevision =
    normalizeConversationAiScheduledStateRevision(incomingConversation);
  return incomingRevision === null || incomingRevision < currentRevision;
};

const AI_SCHEDULED_STATE_KIND = {
  DRAFT: 'draft',
  FAILED: 'failed',
  SCHEDULED: 'scheduled',
};

// Il pending preview esiste solo per i fatti in attesa: un fallimento non ha
// niente da mostrare in anteprima.
const PENDING_PREVIEW_KINDS = [
  AI_SCHEDULED_STATE_KIND.DRAFT,
  AI_SCHEDULED_STATE_KIND.SCHEDULED,
];

const CONVERSATION_PREVIEW_STATE = {
  GENERATING: 'generating',
};

// Esito di una generazione finita senza bozza: l'assistente non aveva le
// informazioni per scrivere e la palla passa all'operatore ("Tocca a te").
// Il backend scrive il flag su additional_attributes e la lista lo riceve
// via conversation.updated; l'evento draft.generation_failed con error_type
// no_draft serve solo a chiudere "sta scrivendo" senza avviso di errore.
const AI_NEEDS_HUMAN_ATTRIBUTE_KEY = 'ai_needs_human';
const AI_NEEDS_HUMAN_STATE_KIND = 'needs_human';
const DRAFT_GENERATION_NO_DRAFT_ERROR_TYPE = 'no_draft';

const hasConversationAiNeedsHuman = (conversation = null) =>
  conversation?.additional_attributes?.[AI_NEEDS_HUMAN_ATTRIBUTE_KEY] === true;

// La lista legge una proiezione dei record reali, non la configurazione della
// inbox e non i vecchi booleani che accorpano draft e scheduled. Se piu' fatti
// coesistono vince quello in attesa (scheduled, poi draft) perche' e' il
// comportamento che avverra' senza un intervento dell'operatore; il fallimento
// (last_failed_at, gia' filtrato dal backend: presente solo finche' nessuna
// risposta e' uscita dopo) si mostra solo quando non c'e' niente in attesa.
const resolveConversationAiScheduledState = (conversation = null) => {
  const state = conversation?.additional_attributes?.ai_scheduled_state;
  if (!state || typeof state !== 'object') {
    return { failedAt: null, kind: '', scheduledAt: null };
  }

  const scheduledAt =
    typeof state.next_scheduled_at === 'string' &&
    state.next_scheduled_at.trim()
      ? state.next_scheduled_at
      : null;

  if (scheduledAt) {
    return {
      failedAt: null,
      kind: AI_SCHEDULED_STATE_KIND.SCHEDULED,
      scheduledAt,
    };
  }
  if (state.has_draft === true) {
    return {
      failedAt: null,
      kind: AI_SCHEDULED_STATE_KIND.DRAFT,
      scheduledAt: null,
    };
  }

  const failedAt =
    typeof state.last_failed_at === 'string' && state.last_failed_at.trim()
      ? state.last_failed_at
      : null;
  if (failedAt) {
    return {
      failedAt,
      kind: AI_SCHEDULED_STATE_KIND.FAILED,
      scheduledAt: null,
    };
  }

  return { failedAt: null, kind: '', scheduledAt: null };
};

const formatConversationScheduledTime = ({
  scheduledAt,
  locale,
  timeZone,
} = {}) => {
  if (!scheduledAt) return '';

  const date = new Date(scheduledAt);
  if (Number.isNaN(date.getTime())) return '';

  try {
    return new Intl.DateTimeFormat(locale || undefined, {
      hour: '2-digit',
      minute: '2-digit',
      ...(timeZone ? { timeZone } : {}),
    }).format(date);
  } catch (_error) {
    return new Intl.DateTimeFormat(locale || undefined, {
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  }
};

const calendarDateParts = ({ date, timeZone } = {}) => {
  const formatter = new Intl.DateTimeFormat('en-CA', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    ...(timeZone ? { timeZone } : {}),
  });
  const parts = Object.fromEntries(
    formatter
      .formatToParts(date)
      .filter(({ type }) => ['day', 'month', 'year'].includes(type))
      .map(({ type, value }) => [type, Number(value)])
  );
  return parts;
};

const formatConversationScheduledLabelParts = ({
  scheduledAt,
  locale,
  timeZone,
  now = new Date(),
} = {}) => {
  const scheduledDate = new Date(scheduledAt);
  const currentDate = now instanceof Date ? now : new Date(now);
  if (
    Number.isNaN(scheduledDate.getTime()) ||
    Number.isNaN(currentDate.getTime())
  ) {
    return { date: '', kind: '', time: '' };
  }

  try {
    const scheduledParts = calendarDateParts({ date: scheduledDate, timeZone });
    const currentParts = calendarDateParts({ date: currentDate, timeZone });
    const scheduledDay = Date.UTC(
      scheduledParts.year,
      scheduledParts.month - 1,
      scheduledParts.day
    );
    const currentDay = Date.UTC(
      currentParts.year,
      currentParts.month - 1,
      currentParts.day
    );
    const dayDifference = Math.round(
      (scheduledDay - currentDay) / (24 * 60 * 60 * 1000)
    );
    const time = formatConversationScheduledTime({
      scheduledAt,
      locale,
      timeZone,
    });

    if (dayDifference === 0) return { date: '', kind: 'today', time };
    if (dayDifference === 1) return { date: '', kind: 'tomorrow', time };

    const includeYear = scheduledParts.year !== currentParts.year;
    const date = new Intl.DateTimeFormat(locale || undefined, {
      day: 'numeric',
      month: 'short',
      ...(includeYear ? { year: 'numeric' } : {}),
      ...(timeZone ? { timeZone } : {}),
    }).format(scheduledDate);
    return { date, kind: 'future', time };
  } catch (_error) {
    return {
      date: '',
      kind: 'today',
      time: formatConversationScheduledTime({ scheduledAt, locale }),
    };
  }
};

const resolveConversationPendingPreview = (conversation = null) => {
  const preview = conversation?.pending_preview;
  if (!preview || typeof preview !== 'object') return null;
  if (!PENDING_PREVIEW_KINDS.includes(preview.kind)) {
    return null;
  }

  return {
    content: typeof preview.content === 'string' ? preview.content : '',
    hasAttachments: preview.has_attachments === true,
    kind: preview.kind,
    scheduledAt: preview.scheduled_at || null,
    sourceId: preview.source_id ?? null,
    sourceType: preview.source_type || '',
    subject: typeof preview.subject === 'string' ? preview.subject : '',
    updatedAt: preview.updated_at || null,
  };
};

const buildConversationPendingPreviewMessage = (preview = null) => {
  if (!preview) return null;
  const text = preview.content || '';

  return {
    id: `pending-${preview.sourceType || 'item'}-${preview.sourceId || 'new'}`,
    content: text,
    preview_content: text,
    processed_message_content: text,
    message_type: 1,
    private: false,
    status: 'pending',
    attachments: [],
  };
};

// "Tocca a te" cede il passo a tutto cio' che e' in corso o in attesa:
// generazione, bozza, invio programmato, invio fallito.
const resolveConversationAiNeedsHumanState = (conversation = null) => {
  if (!hasConversationAiNeedsHuman(conversation)) return null;
  if (isConversationGeneratingDraft(conversation)) return null;
  if (resolveConversationAiScheduledState(conversation).kind) return null;
  if (resolveConversationPendingPreview(conversation)) return null;

  return { failedAt: null, kind: AI_NEEDS_HUMAN_STATE_KIND, scheduledAt: null };
};

const hasPendingConversationDraft = ({
  conversation = null,
  hasScheduledIndicator = hasConversationScheduledIndicator(conversation),
  isGeneratingDraft = isConversationGeneratingDraft(conversation),
} = {}) => {
  if (isGeneratingDraft) return false;
  if (hasScheduledIndicator) return false;

  return (
    !!conversation?.autoDraft ||
    conversation?.additional_attributes?.has_pending_ai_draft === true
  );
};

const buildConversationScheduledFlagPayload = ({
  conversationId,
  flagKey,
  items = [],
} = {}) => ({
  conversationId,
  [flagKey]: hasVisibleScheduledItems(items),
});

export {
  AI_NEEDS_HUMAN_ATTRIBUTE_KEY,
  AI_NEEDS_HUMAN_STATE_KIND,
  DRAFT_GENERATION_NO_DRAFT_ERROR_TYPE,
  AI_SCHEDULED_STATE_ATTRIBUTE_KEY,
  AI_SCHEDULED_STATE_REVISION_ATTRIBUTE_KEY,
  SCHEDULED_EMAIL_FLAG_KEY,
  SCHEDULED_MESSAGE_FLAG_KEY,
  SET_CONVERSATION_HAS_SCHEDULED_EMAIL_MUTATION,
  SET_CONVERSATION_HAS_SCHEDULED_MESSAGE_MUTATION,
  VISIBLE_SCHEDULED_ITEM_STATUSES,
  buildScheduledItemDraftDedupeKey,
  buildConversationScheduledFlagPayload,
  filterVisibleScheduledItems,
  hasConversationAiReply,
  isLastMessageFromAi,
  isMessageFromAi,
  isStaleConversationAiScheduledStateSnapshot,
  normalizeConversationAiScheduledStateRevision,
  AI_SCHEDULED_STATE_KIND,
  CONVERSATION_PREVIEW_STATE,
  buildConversationPendingPreviewMessage,
  formatConversationScheduledLabelParts,
  formatConversationScheduledTime,
  resolveConversationPendingPreview,
  resolveConversationAiScheduledState,
  hasConversationScheduledEmail,
  hasConversationScheduledIndicator,
  hasConversationScheduledMessage,
  hasConversationAiNeedsHuman,
  resolveConversationAiNeedsHumanState,
  hasPendingConversationDraft,
  hasVisibleScheduledItems,
  isConversationGeneratingDraft,
};
